import React, { useEffect, useRef } from "react";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import styles from "./styles.module.scss";
import { useContext } from "../../context";
import { Block } from "./shared/Block";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { SURVEY_TYPE } from "../../../../../../api/surveys/config";
import { FileIcon } from "../../../../../../assets/Icons/FileIcon";
import { ToggleLeftIcon } from "../../../../../../assets/Icons/ToggleLeftIcon";
import { CircleDotIcon } from "../../../../../../assets/Icons/CircleDotIcon";
import { SquareCheckIcon } from "../../../../../../assets/Icons/SquareCheckIcon";
import { RulerDimensionLineIcon } from "../../../../../../assets/Icons/RulerDimensionLineIcon";
import { CloudUploadIcon } from "../../../../../../assets/Icons/CloudUploadIcon";
import { BLOCK_FIELDS, TITLE_ELEMENT_ID } from "../../config";

const ICON_WIDTH = "16px";

const iconsByType = {
  [SURVEY_TYPE.text]: <FileIcon key={SURVEY_TYPE.text} width={ICON_WIDTH} />,
  [SURVEY_TYPE.binary]: (
    <ToggleLeftIcon key={SURVEY_TYPE.binary} width={ICON_WIDTH} />
  ),
  [SURVEY_TYPE.singleChoice]: (
    <CircleDotIcon key={SURVEY_TYPE.singleChoice} width={ICON_WIDTH} />
  ),
  [SURVEY_TYPE.multipleChoice]: (
    <SquareCheckIcon key={SURVEY_TYPE.multipleChoice} width={ICON_WIDTH} />
  ),
  [SURVEY_TYPE.scale]: (
    <RulerDimensionLineIcon key={SURVEY_TYPE.scale} width={ICON_WIDTH} />
  ),
  [SURVEY_TYPE.file]: (
    <CloudUploadIcon key={SURVEY_TYPE.file} width={ICON_WIDTH} />
  ),
};

const SortableItem = SortableElement(({ children }) => children);

const SortableList = SortableContainer(({ items }) => {
  const listRef = useRef(null);
  const { blocks, blockListRef } = useContext();

  useEffect(() => {
    if (!listRef.current) return;
    blockListRef.init(listRef.current);
  }, []);

  return (
    <div ref={listRef} className={styles.list}>
      {items.map((block, index) => {
        const type = block[BLOCK_FIELDS.type];
        return (
          <SortableItem key={block.id} index={index}>
            <Block
              id={block.id}
              required={block[BLOCK_FIELDS.isRequired]}
              markContent={[iconsByType[type], index + 1]}
            >
              <Block.Question
                value={block[BLOCK_FIELDS.question]}
                onChange={(value) =>
                  blocks.update(block.id, BLOCK_FIELDS.question, value)
                }
              />
              {block[BLOCK_FIELDS.isDescriptionRequired] && (
                <Block.Description
                  value={block[BLOCK_FIELDS.description]}
                  onChange={(value) =>
                    blocks.update(block.id, BLOCK_FIELDS.description, value)
                  }
                />
              )}
              {[SURVEY_TYPE.singleChoice, SURVEY_TYPE.multipleChoice].includes(
                type,
              ) && (
                <Block.Choices
                  blockId={block.id}
                  value={block[BLOCK_FIELDS.choices]}
                  onChange={(value) =>
                    blocks.update(block.id, BLOCK_FIELDS.choices, value)
                  }
                />
              )}
            </Block>
          </SortableItem>
        );
      })}
    </div>
  );
});

export const Content = () => {
  const { tSettings: t } = useAppTranslation.Settings({
    keyPrefix: "survey",
  });

  const { layout, title, blocks } = useContext();

  if (layout.preview.isFullView) {
    return null;
  }

  return (
    <div className={styles.root}>
      <Block
        required
        elementId={TITLE_ELEMENT_ID}
        markContent={t("content.title")}
      >
        <input
          value={title.value}
          onChange={(e) => title.set(e.target.value)}
        />
      </Block>
      <SortableList
        axis="y"
        lockAxis="y"
        items={blocks.unwrap()}
        helperClass={styles.disableSelection}
        onSortEnd={({ oldIndex, newIndex }) => {
          blocks.reorder(oldIndex, newIndex);
        }}
      />
    </div>
  );
};
